import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui';
import { GetFunctionsByEventId, AddFunction, AddGuest } from '../../api/api';
import { SIDES, FUNCTION_TYPES, OTHER_FUNCTION_TYPE, normaliseText, isMarriageEventType, isPastFunction } from '../../utils/helpers';

const ten = (v) => String(v || '').replace(/\D/g, '').slice(0, 10);
const emptyToNull = (obj) => {
  const out = {};
  for (const k of Object.keys(obj)) out[k] = obj[k] === '' ? null : obj[k];
  return out;
};

const blankFn = () => ({
  FunctionName: '', FunctionType: '', FunctionTypeOther: '', FunctionDate: '', FunctionTime: '',
  Side: 'Common', Venue: '', City: '', VenueAddress: '', GpsLocation: '',
});
const blankGuest = () => ({
  GuestName: '', MobileNumber: '', Side: 'Common', Relation: '', ExpectedCount: 1,
});
const blankMember = () => ({
  GuestName: '', MobileNumber: '', Side: 'Common', Relation: '', ExpectedCount: 1, FunctionId: '',
});

// Guided 3-step "Setup Event" wizard. All logic lives here; the desktop/mobile
// files only render. It writes to the SAME tables the standalone pages use:
//   Step 1 -> Evt_Functions (AddFunction)
//   Step 2 -> Evt_Guests for the just-created/selected function (AddGuest)
//   Step 3 -> Evt_Guests for the event (members invited per chosen function)
// Step 1 (functions) is admin-only; steps 2-3 follow the guest permission.
export function useSetupWizard(event) {
  const { user, organiser, isAdmin, can } = useAuth();
  const toast = useToast();
  const userId = user.UserId;

  const canFunctions = isAdmin; // Owner / Event Admin only add functions
  const canGuests = can('CanGuestsAdd') || can('CanGuests') || can('CanMembersAdd') || can('CanMembers') || isAdmin;
  const isTwoSided = isMarriageEventType(event?.EventTypeName || event?.EventType);

  const [step, setStep] = useState(1);
  const [functions, setFunctions] = useState([]);
  const [saving, setSaving] = useState(false);

  // The function we're working inside (set after creating one, or picked).
  const [activeFnId, setActiveFnId] = useState('');

  const [fnForm, setFnForm] = useState(blankFn());
  const [guestForm, setGuestForm] = useState(blankGuest());
  const [memberForm, setMemberForm] = useState(blankMember());

  // Running tallies so the user sees progress within the session.
  const [guestsAdded, setGuestsAdded] = useState(0);
  const [membersAdded, setMembersAdded] = useState(0);

  const loadFunctions = useCallback(() => {
    GetFunctionsByEventId({ EventId: event.EventId })
      .then(d => setFunctions(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, [event]);

  useEffect(() => { loadFunctions(); }, [loadFunctions]);

  const activeFn = useMemo(
    () => functions.find(f => String(f.FunctionId) === String(activeFnId)) || null,
    [functions, activeFnId]
  );
  const fnOptions = useMemo(
    () => functions.filter(f => !isPastFunction(f)).map(f => ({ value: f.FunctionId, label: f.FunctionName })), // past functions cannot take new guests
    [functions]
  );

  const FUNCTION_TYPE_OPTIONS = FUNCTION_TYPES;
  const SIDE_OPTIONS = isTwoSided ? SIDES : ['Common'];

  const setFn = (k, v) =>
    setFnForm(f => ({ ...f, [k]: (k === 'FunctionName' || k === 'Venue' || k === 'City') ? normaliseText(v) : v }));
  const setGuest = (k, v) =>
    setGuestForm(f => ({ ...f, [k]: (k === 'GuestName' || k === 'Relation') ? normaliseText(v) : k === 'MobileNumber' ? ten(v) : v }));
  const setMember = (k, v) =>
    setMemberForm(f => ({ ...f, [k]: (k === 'GuestName' || k === 'Relation') ? normaliseText(v) : k === 'MobileNumber' ? ten(v) : v }));

  const addedByName = `${user.Surname || ''} ${user.Name || ''}`.trim() || user.Name || '';

  // ── Step 1: create a function, then advance to step 2 inside it ─────────────
  const saveFunction = async () => {
    if (!String(fnForm.FunctionName).trim()) return toast('Function name is required');
    setSaving(true);
    try {
      const res = await AddFunction({
        ...emptyToNull({ ...fnForm, FunctionTypeOther: undefined,
          FunctionType: fnForm.FunctionType === OTHER_FUNCTION_TYPE ? (String(fnForm.FunctionTypeOther || '').trim() || OTHER_FUNCTION_TYPE) : fnForm.FunctionType }),
        Side: isTwoSided ? fnForm.Side : 'Common',
        EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        Status: 'Planned', Valid: 1, CreatedBy: userId, UpdatedBy: userId,
      });
      const newId = res && res.insertId;
      toast('Function added');
      setFnForm(blankFn());
      loadFunctions();
      if (newId) { setActiveFnId(String(newId)); setStep(2); }
    } catch {
      toast('Could not add function');
    } finally {
      setSaving(false);
    }
  };

  // ── Step 2: add guests into the active function ────────────────────────────
  const saveGuest = async ({ keepGoing = true } = {}) => {
    if (!activeFnId) return toast('Pick a function first');
    if (!String(guestForm.GuestName).trim()) return toast('Guest name is required');
    setSaving(true);
    try {
      await AddGuest({
        ...emptyToNull(guestForm),
        Side: isTwoSided ? guestForm.Side : 'Common',
        ExpectedCount: Number(guestForm.ExpectedCount) || 1,
        EventId: event.EventId, OrganiserId: organiser.OrganiserId, FunctionId: activeFnId,
        InviteStatus: 'Pending', Visibility: 'Common',
        AddedByName: addedByName || null, AddedByMobile: user.MobileNumber || null,
        Valid: 1, CreatedBy: userId, UpdatedBy: userId,
      });
      setGuestsAdded(n => n + 1);
      setGuestForm(blankGuest());
      toast('Guest added');
      if (!keepGoing) setStep(3);
    } catch {
      toast('Could not add guest');
    } finally {
      setSaving(false);
    }
  };

  // ── Step 3: add members for the event (a guest invited to a chosen function) ─
  const saveMember = async ({ keepGoing = true } = {}) => {
    if (!String(memberForm.FunctionId)) return toast('Pick a function for this member');
    if (!String(memberForm.GuestName).trim()) return toast('Member name is required');
    setSaving(true);
    try {
      await AddGuest({
        ...emptyToNull(memberForm),
        Side: isTwoSided ? memberForm.Side : 'Common',
        ExpectedCount: Number(memberForm.ExpectedCount) || 1,
        EventId: event.EventId, OrganiserId: organiser.OrganiserId,
        InviteStatus: 'Pending', Visibility: 'Common',
        AddedByName: addedByName || null, AddedByMobile: user.MobileNumber || null,
        Valid: 1, CreatedBy: userId, UpdatedBy: userId,
      });
      setMembersAdded(n => n + 1);
      setMemberForm(m => ({ ...blankMember(), FunctionId: m.FunctionId })); // keep the function selected
      toast('Member added');
      if (!keepGoing) toast('Setup complete');
    } catch {
      toast('Could not add member');
    } finally {
      setSaving(false);
    }
  };

  // Jump to step 2 using an already-existing function (skip creating one).
  const pickExistingFunction = (id) => { setActiveFnId(String(id)); setStep(2); };

  const goStep = (n) => {
    if (n === 1 && !canFunctions) return; // non-admins can't create functions
    if (n === 2 && !activeFnId && fnOptions.length) setActiveFnId(String(fnOptions[0].value));
    setStep(n);
  };

  return {
    step, goStep, saving,
    canFunctions, canGuests,
    functions, fnOptions, activeFn, activeFnId, setActiveFnId, pickExistingFunction,
    fnForm, setFn, saveFunction,
    guestForm, setGuest, saveGuest, guestsAdded,
    memberForm, setMember, saveMember, membersAdded,
    FUNCTION_TYPE_OPTIONS, SIDE_OPTIONS, isTwoSided,
  };
}
